import React from 'react';
import PropTypes from 'prop-types';
import { DropTarget } from 'react-dnd';
import moment from 'moment';
import globals from '../../../globals/communityGlobals';
import { TREATMENTINSTANCE } from '../TreatmentInstance/TreatmentInstance';
import { ASSIGNMENT } from '../TimelineAssignment/TimelineAssignment';
import { LUNCHBREAK } from '../LunchBreak/LunchBreak';
import { cellLengthInMinutes as cellLength } from '../../../settings/scheduler';

export const dropCellTarget = {
  canDrop(props, monitor) {
    // Check if time have passed, we don't deal with passed assignments
    // Note: Time across the whole JS application is not synced with the Server Time,
    //       we need to address this for all places using current time
    const item = monitor.getItem();

    const { schedulingDisabled } = item;
    if (schedulingDisabled) return false;

    // Check if pre-validation allowes the source
    if (props.targets !== undefined && props.targets.indexOf(item.assignGuid) >= 0) {
      const momentStartDate = moment(props.startDate).utcOffset(0, true);
      const { requiredTime, assignGuid } = item;
      // Functions that return the correct data based on the dragged assignGuid
      // Check TimelineAvailability.jsx > getCellCheckDatesAndTravelTimes for the logic inside
      const { startDateToCheck, previousAssignmentTravelTime } = props.startDateToCheckAndPreviousTravelTime(
        assignGuid,
      );
      const { endDateToCheck, nextAssignmentTravelTime } = props.endDateToCheckAndNextTravelTime(assignGuid);
      const requiredEndDate = momentStartDate.clone().add(requiredTime + nextAssignmentTravelTime, 'minutes');
      const requiredStartDate = momentStartDate.clone().subtract(previousAssignmentTravelTime, 'minutes');

      return (
        moment(requiredEndDate).isSameOrBefore(moment(endDateToCheck).utcOffset(0, true)) &&
        moment(requiredStartDate).isSameOrAfter(moment(startDateToCheck).utcOffset(0, true)) &&
        moment(requiredStartDate).isSameOrAfter(globals.now.utcOffset(0, true))
      );
    }
    return false;
  },

  drop({ postingId, startDate }, monitor) {
    if (monitor.didDrop()) {
      return false;
    }
    return {
      postingId,
      startDate,
    };
  },
};

function collect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    canDrop: monitor.canDrop(),
    isOver: monitor.isOver(),
    isDragging: monitor.isDragging,
    isAnythingDragged: monitor.getItemType() !== null,
  };
}

class TimelineDropCell extends React.Component {
  shouldComponentUpdate(nextProps) {
    return (
      this.props.canDrop !== nextProps.canDrop ||
      this.props.isAnythingDragged !== nextProps.isAnythingDragged ||
      this.props.isOver !== nextProps.isOver ||
      this.props.isAssigned !== nextProps.isAssigned ||
      this.props.targets.join(',') !== nextProps.targets.join(',')
    );
  }
  render() {
    const {
      connectDropTarget,
      canDrop,
      isAssigned,
      isAnythingDragged,
      canAllocateWork,
      startDate,
      isOver,
    } = this.props;
    // Css hovers don't work once drag has started
    const hoverStyle =
      isOver && canDrop
        ? {
            backgroundColor: isAssigned ? '#bdd974' : '#00b1eb',
            border: isAssigned ? '0.5px solid #a0c938' : '0.5px solid #00709f',
          }
        : undefined;
    const assignmentClass = isAssigned ? 'assigned-cell' : '';
    const canDropClass = canDrop ? 'cell-can-drop' : 'cell-cannot-drop';
    const isDragInProgressClass = isAnythingDragged ? 'drag-in-progress' : '';
    const cellStyle = `cell cell-${cellLength} ${assignmentClass} ${canDropClass} ${isDragInProgressClass}`;
    const cell = <div data-start-date={startDate} className={cellStyle} style={hoverStyle} />;
    return canAllocateWork ? connectDropTarget(cell) : cell;
  }
}

TimelineDropCell.defaultProps = {
  isAnythingDragged: false,
  isAssigned: false,
  targets: [],
};

TimelineDropCell.propTypes = {
  canAllocateWork: PropTypes.bool.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  canDrop: PropTypes.bool.isRequired,
  isAnythingDragged: PropTypes.bool,
  isOver: PropTypes.bool.isRequired,
  // Used in drag and drop
  /* eslint-disable react/no-unused-prop-types */
  targets: PropTypes.arrayOf(PropTypes.number),
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  clinicianId: PropTypes.number.isRequired,
  /* eslint-enable */
  isAssigned: PropTypes.bool,
};

export default DropTarget([TREATMENTINSTANCE, ASSIGNMENT, LUNCHBREAK], dropCellTarget, collect)(TimelineDropCell);
